import React from "react";

function WidgetCard({ title, children, empty }) {
  return (
    <div className="ws-widget">
      <h3 className="ws-widget__title">{title}</h3>
      {empty ? <p className="ws-muted">{empty}</p> : children}
    </div>
  );
}

export function AnalyticsWidget({ payload }) {
  const a = (payload && payload.analytics) || {};
  const views =
    a.views_24h != null
      ? a.views_24h
      : a.views != null
        ? a.views
        : a.broadcast_events != null
          ? a.broadcast_events
          : a.content_activity != null
            ? a.content_activity
            : null;
  const label =
    a.views_24h != null || a.views != null
      ? "views (24h)"
      : a.broadcast_events != null
        ? "broadcast events (24h)"
        : "events";
  return (
    <WidgetCard title="Analytics" empty={views == null ? "No analytics yet." : null}>
      {views != null ? (
        <p className="ws-widget__metric">
          <strong>{views}</strong> <span>{label}</span>
        </p>
      ) : null}
    </WidgetCard>
  );
}

export function RecentVideosWidget({ payload }) {
  const recent =
    (payload && (payload.recent_media || payload.recent || payload.videos)) ||
    [];
  const list = Array.isArray(recent) ? recent.slice(0, 5) : [];
  return (
    <WidgetCard
      title="Recent Videos"
      empty={!list.length ? "No recent videos." : null}
    >
      <ul className="ws-widget__list">
        {list.map((v, i) => (
          <li key={v.id || i}>{v.title || v.name || "Untitled"}</li>
        ))}
      </ul>
    </WidgetCard>
  );
}

export function NotificationsWidget({ payload }) {
  const notes = (payload && payload.notifications) || [];
  const list = Array.isArray(notes) ? notes.slice(0, 5) : [];
  return (
    <WidgetCard
      title="Notifications"
      empty={!list.length ? "No notifications." : null}
    >
      <ul className="ws-widget__list">
        {list.map((n, i) => (
          <li key={n.id || i}>
            {n.title || n.message || n.event_type || "Update"}
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
}

export function AccountLinksWidget() {
  return (
    <WidgetCard title="Quick links">
      <ul className="ws-widget__list">
        <li>
          <a href="/account">Account</a>
        </li>
        <li>
          <a href="/payment-history">Payment history</a>
        </li>
        <li>
          <a href="/manage-profiles">Manage profiles</a>
        </li>
        <li>
          <a href="/subscription">Subscriptions</a>
        </li>
      </ul>
    </WidgetCard>
  );
}

export function TeamWidget({ payload }) {
  const team = (payload && (payload.team || payload.members)) || [];
  const list = Array.isArray(team) ? team.slice(0, 5) : [];
  return (
    <WidgetCard
      title="Team"
      empty={!list.length ? "No team members listed." : null}
    >
      <ul className="ws-widget__list">
        {list.map((m, i) => (
          <li key={m.id || i}>{m.name || m.email || "Member"}</li>
        ))}
      </ul>
    </WidgetCard>
  );
}

export function AttentionWidget({ payload }) {
  const counts = (payload && payload.counts) || {};
  const by = counts.by_workflow || {};
  const notes = (payload && payload.notifications) || [];
  const recent = (payload && (payload.recent_media || payload.recent)) || [];
  const org = (payload && payload.organization) || {};
  const items = [];

  if (by.in_review > 0) {
    items.push(`${by.in_review} media item(s) in review`);
  }
  if (by.draft > 0) {
    items.push(`${by.draft} draft upload(s)`);
  }
  if (notes.length) {
    items.push(`${notes.length} notification(s) need attention`);
  }
  if (org.follower_count != null) {
    items.push(`${org.follower_count} followers`);
  }
  if (recent.length) {
    items.push(`Latest upload: ${recent[0].title || "Untitled"}`);
  }

  return (
    <WidgetCard
      title="Needs attention today"
      empty={!items.length ? "You're caught up — nothing urgent." : null}
    >
      <ul className="ws-widget__list">
        {items.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    </WidgetCard>
  );
}

export function ProductionWidget({ payload }) {
  const stats = (payload && (payload.stats || payload.funding)) || {};
  return (
    <WidgetCard title="Production status">
      <p className="ws-widget__metric">
        <strong>{stats.milestones_complete != null ? stats.milestones_complete : "—"}</strong>{" "}
        <span>milestones</span>
      </p>
    </WidgetCard>
  );
}

export function StaffHandoffWidget({ onOpen }) {
  return (
    <WidgetCard title="Full portal">
      <p className="ws-muted">
        Open the full staff tools in the secure Blade portal.
      </p>
      <button type="button" className="ws-btn" onClick={onOpen}>
        Open portal
      </button>
    </WidgetCard>
  );
}

export function AdvertiserCampaignsWidget({ payload }) {
  const items =
    (payload &&
      (payload.advertiser_campaigns ||
        payload.campaigns ||
        (payload.advertiser && payload.advertiser.campaigns))) ||
    [];
  const list = Array.isArray(items) ? items.slice(0, 8) : [];
  return (
    <WidgetCard
      title="My Campaigns"
      empty={!list.length ? "No campaigns assigned to your advertiser account." : null}
    >
      <ul className="ws-widget__list">
        {list.map((c, i) => (
          <li key={c.id || i}>
            {(c.name || "Campaign") +
              " — " +
              (c.status || "unknown") +
              " · " +
              (c.impressions != null ? c.impressions : 0) +
              " imps"}
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
}

export function AdvertiserAnalyticsWidget({ payload }) {
  const summary =
    (payload &&
      (payload.advertiser_analytics ||
        (payload.advertiser && payload.advertiser.analytics) ||
        payload.summary)) ||
    {};
  const impressions = summary.impressions != null ? summary.impressions : summary.impressions_total;
  const clicks = summary.clicks != null ? summary.clicks : summary.clicks_total;
  const empty = impressions == null && clicks == null;
  return (
    <WidgetCard title="Delivery" empty={empty ? "No delivery metrics yet." : null}>
      {!empty ? (
        <p className="ws-widget__metric">
          <strong>{impressions || 0}</strong> <span>impressions</span>
          {" · "}
          <strong>{clicks || 0}</strong> <span>clicks</span>
        </p>
      ) : null}
    </WidgetCard>
  );
}

export const WIDGET_REGISTRY = {
  analytics: AnalyticsWidget,
  recent_videos: RecentVideosWidget,
  notifications: NotificationsWidget,
  account_links: AccountLinksWidget,
  team: TeamWidget,
  attention: AttentionWidget,
  production: ProductionWidget,
  staff_handoff: StaffHandoffWidget,
  advertiser_campaigns: AdvertiserCampaignsWidget,
  advertiser_analytics: AdvertiserAnalyticsWidget,
};

export function WorkspaceDashboard({ workspace, payload, onStaffOpen }) {
  const ids = (workspace && workspace.widgets) || [];
  return (
    <div className="ws-dashboard">
      {ids.map((id) => {
        const Comp = WIDGET_REGISTRY[id];
        if (!Comp) return null;
        if (id === "staff_handoff") {
          return <Comp key={id} onOpen={onStaffOpen} />;
        }
        return <Comp key={id} payload={payload} />;
      })}
    </div>
  );
}

export default WorkspaceDashboard;
